/**
 * For the crew and From the crew — `docs/visit-page.md` §13 (Board 9) and §14
 * (Board 10 A the feed, B a photo opened).
 *
 * For the crew is rolled up, never copied (do-not-substitute 11): a note lives
 * where it is true — the contact, the property, the job (the quote's crew
 * note, kept at approval), the visit — and the page shows each with its source
 * chip and a link home. Internal notes never roll up, and the page says so.
 * The crew never sees money (do-not-substitute 17): nothing here carries any,
 * except a receipt's amount, which is internal.
 */
import { z } from 'zod';
import { PHOTO_TAGS } from './values.js';
// ---------------------------------------------------------------------------
// GET /visits/:id/from-crew?kind=&day= (Board 10)
// ---------------------------------------------------------------------------
export const FROM_CREW_KINDS = ['all', 'photos', 'notes', 'receipts'];
export const fromCrewQuerySchema = z.object({
    kind: z.enum(FROM_CREW_KINDS).default('all'),
    day: z.coerce.number().int().min(1).max(366).optional(),
});
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
/** *Where it lives*. */
export const CREW_NOTE_HOMES = ['visit', 'property', 'contact'];
/**
 * `POST /visits/:id/crew-notes` — **Add note** (§13.3; *Message crew*'s sheet,
 * §7.9): `{text, lives, notify, pin}` (§5.2), with what was attached. *This
 * visit* writes an `activities` note, `visibility = 'crew'`, linked to the work
 * item (pinned when *Tell the crew now*); the others link to the property or
 * the contact.
 */
export const crewNoteSchema = z.object({
    text: z.string().trim().min(1, 'Write the note.').max(4000),
    lives: z.enum(CREW_NOTE_HOMES).default('visit'),
    /** **Tell the crew now** — default on. */
    notify: z.boolean().default(true),
    pin: z.boolean().default(true),
    /** The attach control — files already uploaded, kept crew-visible. */
    fileIds: z.array(z.string().uuid()).max(20).default([]),
});
/** *How far does a change go* (Board 14): **This visit only** · **This one and the 4 after** (D20). */
export const APPLY_TO = ['this', 'this_and_after'];
/**
 * `PATCH /visits/:id/crew-notes/:noteId` — **Edit** on a *This visit* note,
 * and Board 14's *Edit the note* with *Change the crew note for* (a series
 * visit asks how far; *Visits already closed never change*).
 */
export const editCrewNoteSchema = z.object({
    text: z.string().trim().min(1, 'Write the note.').max(4000),
    /** Required on a series visit, ignored on a one-off. */
    applyTo: z.enum(APPLY_TO).default('this'),
});
/** `PATCH /visit-photos/:id {tag?, portal?}` — the lightbox's *Tags* and **Karen sees it on the portal**. */
export const patchPhotoSchema = z.object({
    version: z.number().int().min(1),
    tag: z.enum(PHOTO_TAGS).nullable().optional(),
    portal: z.boolean().optional(),
}).refine((body) => body.tag !== undefined || body.portal !== undefined, { message: 'Nothing to change.' });
/**
 * `POST /visits/:id/crew-files` — For the crew's **Add a file for the crew**:
 * files already uploaded (`POST /api/v1/files`), filed on the visit itself
 * (`file_links.work_item_id`, 0161) and kept crew-visible, so they roll up
 * with the rest (§13.1).
 */
export const addCrewFilesSchema = z.object({
    fileIds: z.array(z.string().uuid()).min(1).max(20),
});
/** `POST /visit-photos/:id/add-to-job` — **Add to the job's files**. */
export const addPhotoToJobSchema = z.object({
    version: z.number().int().min(1),
});
/**
 * `POST /visits/:id/photos` — From the crew's **Upload** and the phone's
 * **Photo**: files already uploaded (`POST /api/v1/files`), linked to the day
 * as internal; an image also writes a `visit_photos` row with `entered_by`
 * (§27.4, `CrewPort.addPhotos`).
 */
export const addPhotosSchema = z.object({
    fileIds: z.array(z.string().uuid()).min(1).max(50),
    /** The day it lands on — default today's, else the first. */
    day: z.number().int().min(1).max(366).optional(),
    tag: z.enum(PHOTO_TAGS).nullish(),
    note: z.string().trim().max(2000).default(''),
});
//# sourceMappingURL=crew.js.map